import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://newslayer.com";
const SITE_NAME = "NewsLayer";
const DEFAULT_TITLE = "NewsLayer — Crypto & Policy News Intelligence";
const DEFAULT_DESC = "Crypto, Web3, DeFi and regulation news intelligence. Breaking stories, live prices and policy tracking in one layer.";

export const absUrl = (path?: string | null) => {
  if (!path) return `${SITE_URL}/logo.png`;
  return path.startsWith("http") ? path : `${SITE_URL}${path}`;
};

const setMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export interface SeoProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string | null;
  type?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
  hreflangs?: { code: string; href: string }[];
}

export const applySeo = (opts: SeoProps) => {
  const title = opts.title ? `${opts.title} — ${SITE_NAME}` : DEFAULT_TITLE;
  const desc = (opts.description || DEFAULT_DESC).slice(0, 300);
  const url = SITE_URL + (opts.path ?? window.location.pathname);
  const img = absUrl(opts.image);
  document.title = title;
  setMeta("name", "description", desc);
  setMeta("name", "robots", opts.noindex ? "noindex, nofollow" : "index, follow");
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
  document.head.querySelectorAll("link[data-seo-hl]").forEach((l) => l.remove());
  for (const alt of opts.hreflangs || []) {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate");
    l.setAttribute("hreflang", alt.code);
    l.setAttribute("href", alt.href);
    l.setAttribute("data-seo-hl", "1");
    document.head.appendChild(l);
  }
  setMeta("property", "og:site_name", SITE_NAME);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:url", url);
  setMeta("property", "og:type", opts.type || "website");
  setMeta("property", "og:image", img);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:image", img);
  document.head.querySelectorAll("script[data-seo-jsonld]").forEach((s) => s.remove());
  const blocks = opts.jsonLd ? (Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd]) : [];
  for (const b of blocks) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo-jsonld", "1");
    s.textContent = JSON.stringify(b);
    document.head.appendChild(s);
  }
};

export const Seo = (props: SeoProps) => {
  const key = JSON.stringify(props);
  useEffect(() => {
    applySeo(props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
};

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "NewsMediaOrganization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
};

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
};

const STATIC_SEO: Record<string, SeoProps> = {
  "/": { jsonLd: [WEBSITE_JSONLD, ORG_JSONLD] },
  "/latest": { title: "Latest Crypto & Web3 News", description: "Every breaking crypto, Web3, DeFi and policy story, published in real time on NewsLayer." },
  "/web3": { title: "Web3 News", description: "The latest Web3 news — dApps, NFTs, gaming, identity and the decentralized internet, updated around the clock." },
  "/defi": { title: "DeFi News", description: "DeFi news and analysis — protocols, yields, TVL moves, exploits and governance, covered as it happens." },
  "/blockchain": { title: "Blockchain News", description: "Blockchain technology news — L1s, L2s, scaling, infrastructure and enterprise adoption." },
  "/crypto": { title: "Crypto News", description: "Crypto market news — Bitcoin, Ethereum, altcoins, exchanges and on-chain trends, updated 24/7." },
  "/policy": { title: "Crypto Policy, Regulation & Jurisdiction Intelligence", description: "Regulation, jurisdictions, enforcement and the policy forces shaping the onchain economy — tracked frameworks and validated policy coverage." },
  "/markets": { title: "Crypto Markets News", description: "Market-moving crypto news — price action, ETFs, institutional flows and macro drivers." },
  "/explainers": { title: "Crypto Explainers", description: "Plain-English explainers on crypto, Web3 and DeFi concepts, protocols and policy." },
  "/research": { title: "Crypto Research", description: "Research-grade analysis of crypto networks, tokens and market structure." },
  "/briefs": { title: "Daily Market Brief Archive", description: "Every NewsLayer Daily Market Brief — the day's five biggest crypto stories, distilled and archived." },
  "/layer-index": { title: "Layer Index — Live Crypto Market Mood", description: "NewsLayer's live 0-100 crypto sentiment gauge — scored from headline sentiment, market momentum and breaking-news activity, with 30 days of history and daily drivers." },
  "/regulation-tracker": { title: "Crypto Regulation Tracker", description: "Track crypto regulation worldwide — bills, rulings, enforcement actions and agency guidance in one live tracker." },
  "/about": { title: "About NewsLayer", description: "Who we are, what we cover and how we hold ourselves accountable — the mission behind NewsLayer's crypto, Web3 and policy coverage.", jsonLd: ORG_JSONLD },
  "/radar": { title: "Regulation Radar — Weekly Crypto Policy Briefing", description: "The premium weekly briefing on crypto regulation: what regulators did, what's on the docket, and what it means for the industry." },
  "/advertise": { title: "Advertise on NewsLayer", description: "Reach crypto-native readers with self-serve ad campaigns — homepage banners, in-feed native units and article placements." },
  "/premium": { title: "NewsLayer Premium", description: "Go Premium for ad-light reading, full article access and member-only intelligence." },
  "/pr": { title: "Press Release Distribution", description: "Publish your Web3 press release on NewsLayer — reviewed, formatted and distributed to crypto readers." },
  "/author-program": { title: "Approved Author Program", description: "Become a NewsLayer approved author — publish stories, build your byline and earn from your readership." },
  "/search": { title: "Search", noindex: true },
  "/my-feed": { title: "My Feed", noindex: true },
  "/login": { title: "Sign In", noindex: true },
  "/signup": { title: "Create Account", noindex: true },
  "/account": { title: "Account", noindex: true },
  "/change-password": { title: "Change Password", noindex: true },
  "/studio": { title: "Author Studio", noindex: true },
  "/payment/success": { title: "Payment Complete", noindex: true },
  "/payment/cancel": { title: "Payment Cancelled", noindex: true },
};

const DYNAMIC_PREFIXES = ["/article/", "/coin/", "/regulation/", "/author/"];
const LANG_ARTICLE_RE = /^\/[a-z]{2}\/article\//;

export const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (DYNAMIC_PREFIXES.some((p) => pathname.startsWith(p)) || LANG_ARTICLE_RE.test(pathname)) return;
    if (pathname.startsWith("/admin")) {
      applySeo({ title: "Admin", noindex: true, path: pathname });
      return;
    }
    applySeo({ ...(STATIC_SEO[pathname] || {}), path: pathname });
  }, [pathname]);
  return null;
};
